import { useEffect, useMemo, useState } from 'react';

import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { type Locale } from '@realadvisor/agency-utils';
import { CheckIcon, CopyIcon, Share2Icon } from 'lucide-react';

import { SlideDialog } from '@modules/_shared/components/SlideDialog';
import {
  type AgentShareType,
  getAgentShareUrl,
} from '@modules/_shared/utils/agentUrls';
import { useLocale } from '@shared/hooks/locale';

type OgData = {
  title: string;
  description: string;
  image: string;
  siteName: string;
  url: string;
};

const getOgMeta = (doc: Document, property: string, fallback: string) =>
  doc
    .querySelector(`meta[property="og:${property}"]`)
    ?.getAttribute('content') ?? fallback;

type ShareDialogProps = {
  open: boolean;
  onClose: () => void;
  shareType?: AgentShareType;
  agentSlug: string;
  tenantId: string;
  locale: Locale;
  reviewId?: string;
  transactionId?: string;
};

export const ShareDialog = ({
  open,
  onClose,
  shareType = 'profile',
  agentSlug,
  tenantId,
  locale,
  reviewId,
  transactionId,
}: ShareDialogProps) => {
  const { t } = useLocale();
  const [copied, setCopied] = useState(false);
  const [ogData, setOgData] = useState<OgData | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const agentShareLink = useMemo(
    () =>
      getAgentShareUrl(tenantId, locale, agentSlug, shareType, {
        reviewId,
        transactionId,
      }),
    [agentSlug, shareType, tenantId, locale, reviewId, transactionId],
  );

  useEffect(() => {
    if (!open || agentShareLink == null || typeof window === 'undefined') {
      return;
    }

    const fetchOgData = async () => {
      setLoadingPreview(true);
      try {
        const response = await fetch(agentShareLink);
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');

        const image = getOgMeta(doc, 'image', '');

        setOgData({
          title: getOgMeta(doc, 'title', ''),
          description: getOgMeta(doc, 'description', ''),
          image,
          siteName: getOgMeta(doc, 'site_name', 'RealAdvisor'),
          url: getOgMeta(doc, 'url', agentShareLink),
        });
        setImageLoaded(!image);
      } catch (err) {
        console.error('Failed to fetch OG data:', err);

        const urlParts = agentShareLink.split('/');
        const fallbackSlug =
          urlParts[urlParts.length - 2]?.replace('agent-', '') ?? 'agent';

        let title = '';
        let description = '';

        switch (shareType) {
          case 'review':
            title = t('Client Review - {{agentSlug}}', {
              agentSlug: fallbackSlug,
            });
            description = t('Read this client review on RealAdvisor');
            break;
          case 'transaction':
            title = t('Recent Transaction - {{agentSlug}}', {
              agentSlug: fallbackSlug,
            });
            description = t('View this successful transaction on RealAdvisor');
            break;
          default:
            title = t('Agent Profile - {{agentSlug}}', {
              agentSlug: fallbackSlug,
            });
            description = t("View this agent's profile on RealAdvisor");
        }

        setOgData({
          title,
          description,
          image: '',
          siteName: 'RealAdvisor',
          url: agentShareLink,
        });
        setImageLoaded(true);
      } finally {
        setLoadingPreview(false);
      }
    };

    fetchOgData();
  }, [open, agentShareLink, shareType, t]);

  const copyToClipboard = async () => {
    if (agentShareLink == null) {
      return;
    }
    try {
      await navigator.clipboard.writeText(agentShareLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const getShareContent = () => {
    switch (shareType) {
      case 'review':
        return {
          title: t('Share Your Review'),
          subtitle: t('Share your client review and build trust'),
          description: t(
            'Copy the link below and share your client review - in emails, text messages, on social media or your website.',
          ),
        };
      case 'transaction':
        return {
          title: t('Share Your Transaction'),
          subtitle: t(
            'Share your successful transaction and showcase your results',
          ),
          description: t(
            'Copy the link below and share your transaction - in emails, text messages, on social media or your website.',
          ),
        };
      default: // profile
        return {
          title: t('Share Profile'),
          subtitle: t('Share in a message and on social media'),
          description: t(
            'Copy the link below and share it anywhere - in emails, text messages, on social media or your website.',
          ),
        };
    }
  };

  const shareContent = getShareContent();

  if (agentShareLink == null) {
    return null;
  }

  return (
    <SlideDialog
      open={open}
      onClose={onClose}
      title={shareContent.title}
      maxWidth="sm"
      fullWidth
    >
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            p: 2,
            bgcolor: 'background.paper',
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
          }}
        >
          <Box
            sx={{
              p: 1.5,
              bgcolor: 'primary.50',
              borderRadius: 2,
            }}
          >
            <Share2Icon size={24} color={'var(--mui-palette-primary-main)'} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {shareContent.title}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
              }}
            >
              {shareContent.subtitle}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            p: 2,
            bgcolor: 'background.paper',
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
            {shareContent.description}
          </Typography>

          <Box
            sx={{
              p: 2,
              bgcolor: 'grey.50',
              borderRadius: 1,
              border: 1,
              borderColor: 'divider',
              mb: 2,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontFamily: 'monospace',
                wordBreak: 'break-all',
                color: 'text.secondary',
              }}
            >
              {agentShareLink}
            </Typography>
          </Box>

          <Button
            onClick={copyToClipboard}
            variant="contained"
            fullWidth
            startIcon={copied ? <CheckIcon /> : <CopyIcon />}
            sx={{ py: 1.5 }}
          >
            {copied ? t('Copied to clipboard') : t('Copy link')}
          </Button>
        </Box>

        <Box
          sx={{
            p: 2,
            bgcolor: 'background.paper',
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
            {t('Social Media Preview')}
          </Typography>

          <Box
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              overflow: 'hidden',
              bgcolor: 'background.paper',
              maxWidth: 600,
              mx: 'auto',
              position: 'relative',
            }}
          >
            {loadingPreview ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: 200,
                  bgcolor: 'grey.100',
                }}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <CircularProgress size={24} sx={{ mb: 1 }} />
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                    }}
                  >
                    {t('Loading preview...')}
                  </Typography>
                </Box>
              </Box>
            ) : ogData ? (
              <>
                {ogData.image && (
                  <Box sx={{ position: 'relative' }}>
                    <img
                      src={ogData.image}
                      alt="Social media preview"
                      style={{
                        width: '100%',
                        height: 'auto',
                        display: 'block',
                      }}
                      onLoad={() => setImageLoaded(true)}
                      onError={() => setImageLoaded(true)}
                    />
                    {!imageLoaded && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          height: 200,
                          bgcolor: 'grey.100',
                        }}
                      >
                        <Box sx={{ textAlign: 'center' }}>
                          <CircularProgress size={24} sx={{ mb: 1 }} />
                          <Typography
                            variant="body2"
                            sx={{
                              color: 'text.secondary',
                            }}
                          >
                            {t('Loading image...')}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </Box>
                )}

                <Box
                  sx={{
                    p: 2,
                    borderTop: 1,
                    borderColor: 'divider',
                    bgcolor: 'grey.50',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      mb: 0.5,
                      display: 'block',
                    }}
                  >
                    {new URL(ogData.url).hostname}
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      mb: 0.5,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {ogData.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {ogData.description}
                  </Typography>
                </Box>
              </>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: 200,
                  bgcolor: 'grey.50',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  {t('Preview unavailable')}
                </Typography>
              </Box>
            )}
          </Box>

          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              mt: 1,
              display: 'block',
            }}
          >
            {t(
              'This is exactly how your profile will appear when shared on Facebook, X (formerly Twitter), LinkedIn, and other social platforms',
            )}
          </Typography>
        </Box>
      </Box>
    </SlideDialog>
  );
};
